import { dehydrate, QueryClient, useQuery } from '@tanstack/react-query'
import { GetStaticPaths, GetStaticProps } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import BasicHead from '../../../../../components/common/BasicHead'
import CsrOnlyStudentBrazePixel from '../../../../../components/common/CsrOnlyStudentBrazePixel'
import DeviceSplit from '../../../../../components/common/DeviceSplit'
import LoadingSpinner from '../../../../../components/common/LoadingSpinner'
import {
  IBlogIndex,
  IMainPostDataQuery,
  IMenuQuery,
  IPostQuery,
  ISubMenuQuery,
} from '../../../../../components/landing/blog/type'
import { GTM_EVENT, sendGTMEvent } from '../../../../../helpers/gtmHelper'
import { logVisitEvent } from '../../../../../modules/braze/logVisitRecord'
import { useCurrentUser } from '../../../../../modules/context/UserContext'
import { IS_DEVELOPMENT, IS_PRODUCTION, RINGLE_DOMAIN } from '../../../../../modules/envVars'
import useIsMobile from '../../../../../modules/hooks/common/useIsMobile'
import {
  LOCALE_EN,
  LOCALE_JA,
  LOCALE_KO,
  LOCALE_VI,
  LOCALE_ZH_CHS,
  LOCALE_ZH_CHT,
  SUPPORTED_LOCALES,
} from '../../../../../modules/i18n/config'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import client from '../../../../../modules/sanityClient'
import { buildSanityImage } from '../../../../../modules/utils'
import {
  FETCH_MAIN_POST_EN_QUERY,
  FETCH_MAIN_POST_JA_QUERY,
  FETCH_MAIN_POST_KO_QUERY,
  FETCH_MAIN_POST_VI_QUERY,
  FETCH_MAIN_POST_ZHS_QUERY,
  FETCH_MAIN_POST_ZHT_QUERY,
  FETCH_MENU_QUERY,
  FETCH_POST_QUERY,
  FETCH_SUB_MENU_QUERY,
} from '../../../../../sanity/groqQueries'
import {
  FETCH_MAIN_POST_KO_QUERYResult,
  FETCH_MENU_QUERYResult,
  FETCH_POST_QUERYResult,
  FETCH_SUB_MENU_QUERYResult,
} from '../../../../../sanity/types'
import { useRestoreScrollOnBack } from '../../../../../store/useScrollRestoreOnBack'

// Dynamically import the BlogHomeDesktop and BlogHomeMobile components
const BlogHomeDesktop = dynamic(() => import('../../../../../components/landing/blog/BlogHomeDesktop'), {
  loading: () => (
    <div className="flex w-screen items-center justify-center">
      <LoadingSpinner lg show primary />
    </div>
  ),
  ssr: false,
})
const BlogHomeMobile = dynamic(() => import('../../../../../components/landing/blog/BlogHomeMobile'), {
  loading: () => (
    <div className="flex h-screen w-screen items-center justify-center">
      <LoadingSpinner lg show primary />
    </div>
  ),
  ssr: false,
})

export const here = {}
// Define the component
const Index = ({ seoData, ogData }: IBlogIndex) => {
  const isMobile = useIsMobile()
  const router = useRouter()
  const { currentUser } = useCurrentUser()
  const { locale } = useTranslation()

  useRestoreScrollOnBack()

  // Query data for posts, menu, subMenu, and mainPostData using useQuery
  const { data: posts }: IPostQuery = useQuery({ queryKey: ['posts'], queryFn: () => fetchPosts(router.query.lang) })
  const { data: menu }: IMenuQuery = useQuery({
    queryKey: ['menu', router.query.lang],
    queryFn: () => fetchMenu(router.query.lang),
  })
  const { data: subMenu }: ISubMenuQuery = useQuery({ queryKey: ['subMenu', router.query.lang], queryFn: fetchSubMenu })
  const { data: mainPostData }: IMainPostDataQuery = useQuery({
    queryKey: ['mainPost', router.query.lang],
    queryFn: () => fetchMainPost(router.query.lang),
  })

  useEffect(() => {
    if (!router) return

    sendGTMEvent(GTM_EVENT.VIEW_CONTENT, {
      page_location: 'Landing_Blog',
    })
  }, [router.asPath])

  return (
    <>
      {/*Meta Data */}
      <BasicHead seoData={seoData} ogData={ogData} />

      <CsrOnlyStudentBrazePixel
        currentUser={currentUser}
        callback={(appBoy) =>
          logVisitEvent(appBoy, 'view_blog', {
            article_name: 'home',
            locale: locale,
          })
        }
      />

      {/* Blog Home Body */}
      <DeviceSplit
        isMobile={isMobile}
        desktop={<BlogHomeDesktop mainPost={mainPostData} posts={posts} menu={menu} subMenu={subMenu} />}
        mobile={<BlogHomeMobile mainPost={mainPostData} posts={posts} menu={menu} subMenu={subMenu} />}
      />
    </>
  )
}
export const fetchPosts = async (lang) => {
  const posts: FETCH_POST_QUERYResult = await client.fetch(FETCH_POST_QUERY, { lang })
  return posts
}

export const getTypeForLanguage = (lang) => {
  switch (lang) {
    case LOCALE_KO:
      return FETCH_MAIN_POST_KO_QUERY
    case LOCALE_EN:
      return FETCH_MAIN_POST_EN_QUERY
    case LOCALE_JA:
      return FETCH_MAIN_POST_JA_QUERY
    case LOCALE_VI:
      return FETCH_MAIN_POST_VI_QUERY
    case LOCALE_ZH_CHS:
      return FETCH_MAIN_POST_ZHS_QUERY
    case LOCALE_ZH_CHT:
      return FETCH_MAIN_POST_ZHT_QUERY
    default:
      return FETCH_MAIN_POST_KO_QUERY
  }
}

export const fetchMainPost = async (lang) => {
  const query = getTypeForLanguage(lang)
  const mainPostData: FETCH_MAIN_POST_KO_QUERYResult = await client.fetch(query)

  return mainPostData
}

export const fetchMenu = async (lang) => {
  const menu: FETCH_MENU_QUERYResult = await client.fetch(FETCH_MENU_QUERY, { lang })
  return menu
}

export const fetchSubMenu = async () => {
  const subMenu: FETCH_SUB_MENU_QUERYResult = await client.fetch(FETCH_SUB_MENU_QUERY)
  return subMenu
}

export const getStaticPaths: GetStaticPaths = () => {
  if (!IS_PRODUCTION) {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }
  const paths = SUPPORTED_LOCALES.map((locale) => ({
    params: { lang: locale },
  }))

  return { paths, fallback: 'blocking' }
}
// Get server-side props function
export const getStaticProps: GetStaticProps = async (props) => {
  const queryClient = new QueryClient()
  const { params } = props

  // Fetch mainPostData before prefetching other queries
  const mainPostData = await fetchMainPost(params.lang)

  // Prefetch queries using queryClient.prefetchQuery
  await Promise.all([
    queryClient.prefetchQuery(['posts', 0, 0, 0, 8, params.lang], () => fetchPosts(params.lang)),
    queryClient.prefetchQuery(['menu', params.lang], () => fetchMenu(params.lang)),
    queryClient.prefetchQuery(['subMenu', params.lang], fetchSubMenu),
    queryClient.prefetchQuery(['mainPost', params.lang], () => Promise.resolve(mainPostData)), // using fetched data
  ])

  // Extract metaData from mainPostData for SEO
  const metaData: any = mainPostData[0]?.metaData || null

  // Define SEO and Open Graph data
  const seoData = {
    pageTitle: metaData?.metaTitle,
    pageDescription: metaData?.metaDescription,
    pageKeywords: metaData?.metaKeyword,
  }

  const ogData = {
    title: metaData?.ogTitle,
    desc: metaData?.ogDescription,
    image: buildSanityImage(mainPostData[0].ogImage as any).url(),
    ogUrl: `https://${IS_DEVELOPMENT ? 'staging.ringlefront.com' : RINGLE_DOMAIN}/ko/student/landing/blog`,
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      mainPostData,
      seoData,
      ogData,
    },
    revalidate: 900,
  }
}

export default Index
